import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import AuditCheckoutPopup from '@/components/AuditCheckoutPopup';
import ConnectedApps from '@/components/ConnectedApps';
import { 
  User, CreditCard, HelpCircle, Crown, Check, Mail, MessageCircle, 
  FileText, Zap, Calendar, Download, ArrowRight, ArrowUpRight, CheckCircle, Loader2,
  Palette, Upload, X, Lock, Image, Eye, TrendingUp, Trophy, Clock, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { PLAN_PRICES, ADDON_PRICES } from '@/config/pricing';
import '@/styles/forms.css';
import { checkoutFromSearch } from '@/lib/checkoutLink';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample audit data for PDF preview — grounded-report shape (findings
// carry evidence; scores intentionally non-uniform)
const sampleAuditForPreview = {
  audit_id: 'sample_preview',
  app_name: 'Sample App',
  overall_score: 58,
  nav_score: 81,
  visual_score: 66,
  friction_score: 44,
  a11y_score: 52,
  conv_score: 63,
  created_at: new Date().toISOString(),
  findings: [
    { title: 'Primary CTA fails contrast at 2.7:1', severity: 'critical',
      description: 'The "Start free trial" button renders at a measured 2.7:1 contrast ratio.',
      recommendation: 'Darken the button label to meet 4.5:1',
      evidence: { source: 'measurement', quote: 'contrast 2.7:1 — #ff9d7a on #faf9f5, 16px' },
      user_impact: 'The page\'s only conversion action is hard to read for low-vision visitors.',
      dimension: 'conversion_design', effort: 'low', impact: 'high' },
    { title: 'Contact form requires 9 fields', severity: 'major',
      description: 'The quote form asks for 9 inputs including fax number before submission.',
      recommendation: 'Cut to name, email, and message',
      evidence: { source: 'html', quote: 'Fields: Name, Company, Role, Phone, Fax, Email, Budget, Timeline, Message' },
      user_impact: 'Every extra required field adds friction at the page\'s single conversion point.',
      dimension: 'friction_points', effort: 'medium', impact: 'high' },
    { title: '12 of 18 images missing alt text', severity: 'major',
      description: 'Parsed HTML shows 12 of 18 images without alt attributes.',
      recommendation: 'Add descriptive alt text to the 12 flagged images',
      evidence: { source: 'html', quote: 'Images Without Alt Text: 12 of 18' },
      user_impact: 'Screen-reader users get nothing from two-thirds of the page\'s imagery.',
      dimension: 'accessibility', effort: 'low', impact: 'high' },
  ],
  dimension_summaries: {
    navigation: 'Navigation on this page is clear and well-labeled.',
    visual_hierarchy: 'The hero carries three competing focal points.',
    friction_points: 'The 9-field quote form is the page\'s largest friction source.',
    accessibility: 'Measured contrast failures and missing alt text hold this score down.',
    conversion_design: 'The primary CTA is visually weak at its measured contrast.',
    mobile_ux: 'Layout holds at 390px with no horizontal overflow.'
  }
};

const plans = [
  {
    id: 'FREE', name: 'Free', period: '', audits: PLAN_PRICES.FREE.quota,
    features: ['Basic report', '6 UX dimensions scored'],
    priceUsd: PLAN_PRICES.FREE.usd, priceInr: PLAN_PRICES.FREE.inr,
  },
  {
    id: 'STARTER', name: 'Starter', period: '/audit', audits: 'Per audit',
    features: ['Full detailed report', 'PDF download', 'Annotated screenshots', 'Fix recommendations', 'Severity prioritization'],
    priceUsd: PLAN_PRICES.STARTER.usd, priceInr: PLAN_PRICES.STARTER.inr,
  },
];

const helpTopics = [
  { title: 'Getting Started', desc: 'Learn how to run your first UX audit', icon: Zap, link: '#' },
  { title: 'Understanding Scores', desc: 'What do the 6 dimension scores mean?', icon: FileText, link: '#' },
  { title: 'Billing & Plans', desc: 'Manage subscriptions and payments', icon: CreditCard, link: '#' },
  { title: 'Contact Support', desc: 'Get help from our team', icon: MessageCircle, link: 'mailto:support@uxauditpro.com' },
];

export default function AccountPage() {
  const { user, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const validTabs = ['profile', 'plans', 'white-label', 'billing', 'help'];
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(validTabs.includes(tabParam) ? tabParam : 'profile');
  const [userPlan, setUserPlan] = useState('FREE');
  const [purchases, setPurchases] = useState([]);
  const [credits, setCredits] = useState(null);      // { available, payment_session_id }
  const [subscription, setSubscription] = useState(null); // { has_subscription, subscription }
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [upgrading, setUpgrading] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // User stats state
  const [userStats, setUserStats] = useState({
    total_audits: 0,
    avg_score: 0,
    best_score: 0,
    score_improvement: 0,
    last_audit_date: null,
    last_audit_app: null
  });
  const [loadingStats, setLoadingStats] = useState(true);
  
  // White-label state
  const [whiteLabelSettings, setWhiteLabelSettings] = useState({
    agency_name: '',
    agency_logo: '',
    enabled: false
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [savingWhiteLabel, setSavingWhiteLabel] = useState(false);
  const [whiteLabelSuccess, setWhiteLabelSuccess] = useState('');
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const fileInputRef = useRef(null);

  const [userAddOns, setUserAddOns] = useState({});
  const [limitState, setLimitState] = useState(null);
  const canUseWhiteLabel = userAddOns.white_label === true;

  /**
   * WHAT THE ACCOUNT ACTUALLY GETS, not what its flag is called.
   *
   * `plan` records "has ever paid" -- one completed payment writes STARTER
   * permanently and nothing resets it. From 2026-09-01 STARTER carries the
   * same monthly free audit as FREE, so the two entitlements are IDENTICAL
   * and this string is deliberately the same for both. A page that said
   * "STARTER" over a crown was describing the flag; this describes the
   * product.
   */
  const ENTITLEMENT = `1 free per month, then $${PLAN_PRICES.STARTER.usd} each`;

  /**
   * Signup month from the record, or nothing.
   *
   * This was the literal 'March 2026' for every account -- a fabricated fact
   * on a page where the customer is the one person who knows it is wrong.
   * `created_at` is written at account creation (server.py, google_auth.py)
   * and /auth/me returns the whole user document, so no new field is needed.
   *
   * Accounts predating the field render an em dash rather than a guess: an
   * absent date is a smaller lie than a plausible one.
   */
  const memberSince = (() => {
    const raw = user?.created_at;
    if (!raw) return '\u2014';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '\u2014';
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  })();

  /**
   * The renewal sentence, from check_audit_limit's own answer.
   *
   * `free_this_month` is set on the grant; `monthly_limit` with `resets_at`
   * on the refusal. Both come from the enforcement path, so this line cannot
   * disagree with what the audit form will do.
   */
  const allowanceLine = (() => {
    if (!limitState) return null;
    if (limitState.free_this_month) {
      return `Your free audit is available now. After that, audits are `
        + `$${PLAN_PRICES.STARTER.usd} each with no monthly cap.`;
    }
    if (limitState.reason === 'monthly_limit' && limitState.resets_at) {
      const d = new Date(limitState.resets_at);
      const when = Number.isNaN(d.getTime())
        ? 'the 1st'
        : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
      return `Your next free audit unlocks on ${when}. Audits are `
        + `$${PLAN_PRICES.STARTER.usd} each in the meantime.`;
    }
    return `Audits are $${PLAN_PRICES.STARTER.usd} each, with no monthly cap.`;
  })();

  /**
   * THE PLAN LINE. Free, unless a RECURRING subscription is active.
   *
   * `user.plan` is not the answer and must not be read here. It records "has
   * ever paid": one completed payment writes STARTER permanently
   * (paypal_routes.activate_subscription) and nothing resets it, which is why
   * the Starter card carried a green "Current" badge forever for anyone who had
   * ever bought a single audit -- asserting a subscription that does not exist.
   *
   * The $49 Starter audit is a ONE-OFF PURCHASE, not a plan. It appears under
   * Purchases below, where a purchase belongs.
   *
   * Derived from db.subscriptions via GET /api/payments/subscription-status,
   * which is the only thing in the system that knows about a recurring charge.
   * Nothing sells a subscription today, so this reads "Free" for everyone --
   * and it will read the real tier, without a code change here, on the day
   * Watch or Agency starts writing that collection.
   */
  const planLine = (subscription?.has_subscription
    && subscription?.subscription?.plan) || 'Free';

  /**
   * THE CREDITS LINE, from the payments the enforcer would accept.
   *
   * `credits.available` counts settled payments that can still be spent, and
   * the rule behind it is lifted from create_audit's own reuse guard
   * (delivery.payment_is_spendable): unstamped, or stamped to an audit that
   * FAILED. Counting "payments with no used_for_audit" alone would under-report
   * a customer whose paid audit failed, who IS owed a re-run -- and a number on
   * this page that the audit form then refuses is the two-predicates-for-one-
   * question shape this codebase has paid for twice.
   *
   * null means the request failed, which is NOT zero. Saying "No paid audits
   * left" because a fetch 404'd is the defect this page just came out of.
   */
  const creditsLine = (() => {
    if (purchases === null || !credits) return null;
    const n = credits.available || 0;
    if (n === 1) return '1 paid audit ready to use';
    if (n > 1) return `${n} paid audits ready to use`;
    return 'No paid audits left';
  })();

  const hasCredit = (credits?.available || 0) > 0;

  const [showAuditCheckout, setShowAuditCheckout] = useState(false);
  const [gateway, setGateway] = useState('paypal'); // 'paypal' | 'razorpay'
  // The "checkout didn't complete" email links here with ?checkout=starter
  // and the add-ons that checkout had; open it as it was.
  const [checkoutAddOns] = useState(() => checkoutFromSearch(window.location.search));
  useEffect(() => { if (checkoutAddOns) setShowAuditCheckout(true); }, [checkoutAddOns]);

  // Standalone white label add-on purchase (from locked tab)
  const [purchasingAddon, setPurchasingAddon] = useState(false);
  // Disable add-on
  const [disablingAddon, setDisablingAddon] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState(false);

  // Check for payment success (handles white-label addon return from PayPal)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const paymentId = params.get('paymentId');
    const payerId = params.get('PayerID');

    if (payment === 'success' && paymentId && payerId) {
      // Execute one-time payment (e.g. white-label addon) and refresh user data
      const executeAndRefresh = async () => {
        try {
          await axios.post(`${API}/payments/execute-payment`, {
            paymentId, PayerID: payerId
          }, { withCredentials: true });
        } catch (e) {
          console.error('Payment execution failed:', e);
        }
        try {
          const meResp = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUserPlan(meResp.data.plan || 'FREE');
          setUserAddOns(meResp.data.add_ons || {});
          setUser(meResp.data);
          setPaymentSuccess(true);
          setTimeout(() => setPaymentSuccess(false), 5000);
        } catch (e) {
          console.error('Failed to refresh user after payment:', e);
        }
        window.history.replaceState({}, '', '/account?tab=white-label');
      };
      executeAndRefresh();
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Fire all requests in parallel; each fails independently
      const [meResp, billResp, statsResp, wlResp, gwResp, limitResp, subResp] =
        await Promise.allSettled([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/payments/history`, { withCredentials: true }),
        axios.get(`${API}/auth/me/stats`, { withCredentials: true }),
        axios.get(`${API}/settings/white-label`, { withCredentials: true }),
        axios.post(`${API}/payments/detect-gateway`, {}, { withCredentials: true }),
        axios.get(`${API}/audits/check-limit`, { withCredentials: true }),
        // Whether a RECURRING subscription is active. Separate from `plan`,
        // which records "has ever paid" and is not a subscription state.
        axios.get(`${API}/payments/subscription-status`, { withCredentials: true }),
      ]);

      if (gwResp.status === 'fulfilled') {
        setGateway(gwResp.value.data.gateway || 'paypal');
      }

      // The allowance state, from the endpoint that ENFORCES it. The plan
      // name is not a proxy for it: after 2026-09-01 FREE and STARTER carry
      // the same monthly audit, and the only thing that knows whether this
      // month's is still there is check_audit_limit.
      if (limitResp.status === 'fulfilled') {
        setLimitState(limitResp.value.data || null);
      }

      if (meResp.status === 'fulfilled') {
        setUserPlan(meResp.value.data.plan || 'FREE');
        setUserAddOns(meResp.value.data.add_ons || {});
        setUser(meResp.value.data);
      } else {
        console.error('Failed to fetch user:', meResp.reason);
      }

      // `purchases` and `credits`, from GET /api/payments/history. That route
      // did not exist until 2026-10-02: this call 404'd, allSettled swallowed
      // it, and the Billing History tab showed "No billing history yet /
      // Upgrade to a paid plan to see your invoices here" to EVERY customer,
      // including ones who had paid. Nothing is defaulted to an empty list on
      // failure any more -- a request that fails is reported as unknown, not
      // as "you have never bought anything".
      if (billResp.status === 'fulfilled') {
        setPurchases(billResp.value.data.purchases || []);
        setCredits(billResp.value.data.credits || null);
      } else {
        console.error('Failed to fetch purchase history:', billResp.reason);
        setPurchases(null);
      }

      if (subResp.status === 'fulfilled') {
        setSubscription(subResp.value.data || null);
      }

      if (statsResp.status === 'fulfilled') {
        setUserStats(statsResp.value.data);
      } else {
        console.error('Failed to fetch user stats:', statsResp.reason);
      }
      setLoadingStats(false);

      if (wlResp.status === 'fulfilled' && wlResp.value.data) {
        setWhiteLabelSettings(wlResp.value.data);
        if (wlResp.value.data.agency_logo) {
          setLogoPreview(wlResp.value.data.agency_logo.startsWith('data:')
            ? wlResp.value.data.agency_logo
            : `data:image/png;base64,${wlResp.value.data.agency_logo}`);
        }
      }
    };
    fetchData();
  }, []);

  const handleApplyPromo = async () => {
    setPromoError('');
    setPromoSuccess('');
    if (!promoCode.trim()) return;
    
    try {
      const resp = await axios.post(`${API}/payments/validate-promo`, { code: promoCode }, { withCredentials: true });
      setPromoDiscount(resp.data.discount_percent);
      setPromoSuccess(`${resp.data.discount_percent}% discount applied!`);
    } catch (e) {
      setPromoError(e.response?.data?.detail || 'Invalid promo code');
      setPromoDiscount(0);
    }
  };

  // Called after all upsell popups are resolved
  const doUpgrade = async (planId, addOns) => {
    setUpgrading(planId);
    try {
      const resp = await axios.post(`${API}/payments/create-order`, {
        plan: planId,
        origin_url: window.location.origin,
        promo_code: promoCode || null,
        add_ons: addOns
      }, { withCredentials: true });
      if (resp.data.approval_url) window.location.href = resp.data.approval_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || d).join(', ')
        : typeof detail === 'string'
        ? detail
        : 'Failed to initiate payment. Please try again.';
      alert(msg);
      setUpgrading(null);
    }
  };

  const handleUpgrade = (planId) => {
    if (planId === 'FREE') return;
    setShowAuditCheckout(true);
  };

  // Standalone white label add-on purchase (user is already on a paid plan)
  const handlePurchaseWhiteLabelAddon = async () => {
    if (userPlan === 'FREE') { setActiveTab('plans'); return; }
    setPurchasingAddon(true);
    try {
      const resp = await axios.post(`${API}/payments/create-order`, {
        plan: 'STARTER',           // reuse Starter order type for the add-on charge
        origin_url: window.location.origin,
        add_ons: { white_label: true },
        return_path: '/account?tab=white-label&payment=success',
      }, { withCredentials: true });
      if (resp.data.approval_url) window.location.href = resp.data.approval_url;
      else alert('Failed to initiate payment. Please try again.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPurchasingAddon(false);
    }
  };

  // Disable white label add-on
  const handleDisableWhiteLabelAddon = async () => {
    setDisablingAddon(true);
    try {
      await axios.delete(`${API}/payments/addon/white-label`, { withCredentials: true });
      setUserAddOns(prev => ({ ...prev, white_label: false }));
      setDisableConfirm(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to disable add-on');
    } finally {
      setDisablingAddon(false);
    }
  };

  // Handle subscription return from PayPal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscriptionStatus = params.get('subscription');
    const subscriptionId = params.get('subscription_id');
    const token = params.get('token'); // Legacy support
    const plan = params.get('plan');
    
    if (subscriptionStatus === 'success' && (subscriptionId || token)) {
      // Execute the subscription
      const executeSubscription = async () => {
        try {
          const execResp = await axios.post(`${API}/payments/execute-subscription`, {
            subscription_id: subscriptionId || token
          }, { withCredentials: true });

          // Refetch user data immediately so the plan shows without a full reload
          const meResp = await axios.get(`${API}/auth/me`, { withCredentials: true });
          // Falls back to FREE, not 'PRO'. Defaulting an unknown response to
          // the highest tier is the frontend half of the grandfather branch
          // deleted from server.py on 2026-08-31: both handed out entitlement
          // on the strength of not knowing.
          setUserPlan(meResp.data.plan || execResp.data.plan || plan || 'FREE');
          setUserAddOns(meResp.data.add_ons || {});
          setUser(meResp.data);

          window.history.replaceState({}, '', '/account?tab=plans');
          setActiveTab('plans');
          setPaymentSuccess(true);
          setTimeout(() => setPaymentSuccess(false), 5000);
        } catch (e) {
          console.error('Subscription execution failed:', e);
          alert('Failed to activate subscription. Please contact support.');
          window.history.replaceState({}, '', '/account?tab=plans');
        }
      };
      executeSubscription();
    }
  }, []);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result;
      setLogoPreview(base64);
      setWhiteLabelSettings(prev => ({ ...prev, agency_logo: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setWhiteLabelSettings(prev => ({ ...prev, agency_logo: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveWhiteLabel = async () => {
    if (!canUseWhiteLabel) return;
    
    setSavingWhiteLabel(true);
    setWhiteLabelSuccess('');
    
    try {
      await axios.post(`${API}/settings/white-label`, {
        agency_name: whiteLabelSettings.agency_name,
        agency_logo: whiteLabelSettings.agency_logo,
        enabled: whiteLabelSettings.enabled
      }, { withCredentials: true });
      
      setWhiteLabelSuccess('Settings saved successfully!');
      setTimeout(() => setWhiteLabelSuccess(''), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingWhiteLabel(false);
    }
  };

  const handlePreviewPDF = async () => {
    if (!canUseWhiteLabel) return;
    
    setGeneratingPreview(true);
    
    try {
      // First save the current settings
      await axios.post(`${API}/settings/white-label`, {
        agency_name: whiteLabelSettings.agency_name,
        agency_logo: whiteLabelSettings.agency_logo,
        enabled: whiteLabelSettings.enabled
      }, { withCredentials: true });
      
      // Then request a preview PDF with current settings
      const resp = await axios.post(`${API}/settings/white-label/preview-pdf`, {
        sample_audit: sampleAuditForPreview,
        agency_name: whiteLabelSettings.agency_name,
        agency_logo: whiteLabelSettings.agency_logo
      }, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create a download link
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab for preview
      window.open(url, '_blank');
      
      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setGeneratingPreview(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'plans', label: 'Plans', icon: Crown },
    { id: 'white-label', label: 'White-label', icon: Palette },
    { id: 'billing', label: 'Billing History', icon: CreditCard },
    { id: 'help', label: 'Help & Support', icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      <SEO
        title="My Account"
        description="Manage your UXAuditPro account, billing, and white-label settings."
        canonical="/account"
        noIndex={true}
      />
      <AuditCheckoutPopup
        visible={showAuditCheckout}
        onClose={() => setShowAuditCheckout(false)}
        user={user}
        setUser={(u) => { setUser(u); setUserPlan(u?.plan || 'FREE'); }}
        source={checkoutAddOns ? 'checkout_incomplete_email' : 'pricing_page'}
        initialAddOns={checkoutAddOns}
      />

      <div className="max-w-5xl mx-auto px-6">

        {/* Payment Success Banner */}
        {paymentSuccess && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: 'var(--success-bg)', border: '1px solid rgba(5, 150, 105, 0.2)' }}
          >
            <CheckCircle className="w-5 h-5" style={{ color: 'var(--success)' }} />
            <div>
              <p className="font-semibold" style={{ color: 'var(--success)' }}>Payment Successful!</p>
              {/* NOT "upgraded to {userPlan}". Buying an audit grants THAT
                  AUDIT; it also writes plan=STARTER, permanently, and from
                  2026-09-01 that flag carries the same monthly allowance as
                  FREE. So "upgraded" named a tier change that does not happen
                  and an entitlement change that is not one. This says what the
                  payment actually bought. */}
              <p className="text-sm" style={{ color: 'var(--ink-2)' }} data-testid="payment-success-detail">
                Payment received. Your audit is unlocked.
              </p>
            </div>
          </motion.div>
        )}
        
        <div>
          <h1 style={{ fontSize: 'var(--type-h2-size)', fontWeight: 'var(--type-h2-weight)', lineHeight: 'var(--type-h2-leading)', letterSpacing: 'var(--type-h2-tracking)', color: 'var(--ink-1)' }} className="mb-2">My Account</h1>
          <p className="text-sm mb-8" style={{ color: 'var(--ink-2)' }}>Manage your profile, plan, and billing</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
              <div className="flex items-center gap-3 pb-4 mb-4" style={{ borderBottom: '1px solid var(--border-1)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--surface-panel)' }}>
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-6 h-6" style={{ color: 'var(--ink-2)' }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" style={{ color: 'var(--ink-1)' }}>{user?.name || 'User'}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--ink-2)' }}>{user?.email}</p>
                </div>
              </div>
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                    style={{ 
                      backgroundColor: activeTab === tab.id ? 'var(--accent-text)' : 'transparent',
                      color: activeTab === tab.id ? 'white' : 'var(--ink-2)',
                    }}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {tab.id === 'white-label' && !canUseWhiteLabel && (
                      <Lock className="w-3 h-3 ml-auto" style={{ color: 'var(--ink-2)' }} />
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="rounded-2xl p-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                  <h2 className="font-semibold mb-6" style={{ color: 'var(--ink-1)' }}>Profile Information</h2>
                  <div className="space-y-4">
                    {[
                      ['Name', user?.name || 'Not set'],
                      ['Email', user?.email],
                      // `planLine` is derived from db.subscriptions, NOT from
                      // user.plan -- see its definition. The $49 audit is a
                      // purchase and appears under Purchases, not here.
                      ['Plan', planLine],
                      // The entitlement is what the customer came here to
                      // learn, and it is the same on both plan flags.
                      ['Audits', ENTITLEMENT],
                      ['Member Since', memberSince],
                    ].map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between py-3" style={{ borderBottom: '1px solid var(--border-1)' }}>
                        <span className="text-sm" style={{ color: 'var(--ink-2)' }}>{label}</span>
                        {/* NO CROWN, NO ACCENT. The third tuple element used to
                            mark the plan row and pulled in both. Every row here
                            is now a fact about the account rather than a status
                            to decorate, so they render identically. */}
                        <span className="text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl p-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                  <h2 className="font-semibold mb-4" style={{ color: 'var(--ink-1)' }}>Quick Stats</h2>
                  {loadingStats ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--ink-2)' }} />
                    </div>
                  ) : userStats.total_audits === 0 ? (
                    <div className="text-center py-6">
                      <BarChart3 className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--ink-2)' }} />
                      <p className="text-sm mb-3" style={{ color: 'var(--ink-2)' }}>No audits yet</p>
                      <Link to="/audit/new" className="text-sm font-semibold hover:underline" style={{ color: 'var(--accent-text)' }}>
                        Run your first audit
                      </Link>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {[
                          { icon: FileText, value: userStats.total_audits, label: 'Total Audits', color: '#4A9EFF' },
                          { icon: BarChart3, value: userStats.avg_score, label: 'Avg Score', color: '#4A9EFF' },
                          { icon: Trophy, value: userStats.best_score, label: 'Best Score', color: 'var(--severity-major-text)' },
                          { icon: TrendingUp, value: `${userStats.score_improvement >= 0 ? '+' : ''}${userStats.score_improvement}`, label: 'Score Change', color: userStats.score_improvement >= 0 ? 'var(--success)' : 'var(--danger)' },
                        ].map((stat, i) => (
                          <div key={i} className="text-center p-4 rounded-xl" style={{ background: 'var(--surface-panel)' }}>
                            <div className="flex items-center justify-center gap-1.5 mb-1">
                              <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
                              <span className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</span>
                            </div>
                            <p className="text-xs" style={{ color: 'var(--ink-2)' }}>{stat.label}</p>
                          </div>
                        ))}
                      </div>
                      
                      {userStats.last_audit_date && (
                        <div className="pt-4" style={{ borderTop: '1px solid var(--border-1)' }}>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                            <span style={{ color: 'var(--ink-2)' }}>Last Audit:</span>
                            <span className="font-medium" style={{ color: 'var(--ink-1)' }}>{userStats.last_audit_app}</span>
                            <span style={{ color: 'var(--ink-2)' }}>·</span>
                            <span style={{ color: 'var(--ink-2)' }}>
                              {new Date(userStats.last_audit_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <ConnectedApps />
              </motion.div>
            )}

            {/* Plans Tab */}
            {activeTab === 'plans' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="rounded-2xl p-6 mb-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                  {/* NO PLAN PILL. It read {userPlan} in accent on a tint --
                      a badge for a tier, next to a heading naming the same
                      tier. From 2026-09-01 FREE and STARTER carry the same
                      monthly audit, so the badge marks a distinction the
                      product no longer makes. Relabelling it "Paid customer"
                      was considered and rejected: it invites "as opposed to
                      what?" and the honest answer is "nothing, same
                      entitlement". */}
                  <div className="mb-6">
                    <h2 className="font-semibold" style={{ color: 'var(--ink-1)' }}>Your audits</h2>
                    {allowanceLine && (
                      <p className="text-sm" style={{ color: 'var(--ink-2)' }} data-testid="allowance-line">
                        {allowanceLine}
                      </p>
                    )}
                    {/* THE CREDITS LINE. Separate from allowanceLine on
                        purpose: that one is about the monthly FREE audit, this
                        one is about audits already BOUGHT. Conflating them is
                        how a customer holding a paid credit gets told to pay. */}
                    {creditsLine && (
                      <p className="text-sm mt-1" style={{ color: 'var(--ink-1)' }} data-testid="credits-line">
                        {creditsLine}
                      </p>
                    )}
                    {purchases === null && (
                      <p className="text-sm mt-1" style={{ color: 'var(--danger)' }} data-testid="credits-unknown">
                        We could not load your purchases just now, so this does not
                        mean you have none. Reload, or contact us if it persists.
                      </p>
                    )}
                    {/* THE UPGRADE CTA, only when there is nothing to spend.
                        Offering "Buy an audit" to someone holding an unused one
                        sells them a second copy of what they already own. */}
                    {creditsLine && !hasCredit && (
                      <button
                        onClick={() => setShowAuditCheckout(true)}
                        className="uxa-submit uxa-submit--auto uxa-submit--tertiary mt-3"
                        data-testid="buy-an-audit-cta"
                      >
                        Buy an audit, ${PLAN_PRICES.STARTER.usd}
                        <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </button>
                    )}
                    {hasCredit && (
                      <Link
                        to="/audit/new"
                        className="uxa-submit uxa-submit--auto uxa-submit--tertiary mt-3"
                        data-testid="use-paid-audit-cta"
                      >
                        Use it now
                        <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                  
                  {/* Promo Code Input */}
                  <div className="p-4 rounded-xl" style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-1)' }}>
                    <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--ink-2)' }}>Have a promo code?</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="flex-1 px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent-text)]/30"
                        style={{ background: 'var(--surface-white)', border: '1px solid var(--border-2)', color: 'var(--ink-1)' }}
                        data-testid="promo-code-input"
                      />
                      <button
                        onClick={handleApplyPromo}
                        className="uxa-submit uxa-submit--auto uxa-submit--tertiary"
                        data-testid="apply-promo-btn"
                      >
                        Apply
                        <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                    {promoError && <p className="text-xs mt-2" style={{ color: 'var(--danger)' }}>{promoError}</p>}
                    {promoSuccess && <p className="text-xs mt-2" style={{ color: 'var(--success)' }}>{promoSuccess}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {plans.map((plan) => {
                    // THE BADGE THAT LIED, FIXED 2026-10-02.
                    //
                    // This was `userPlan === plan.id`. `user.plan` records
                    // "has ever paid" and is written to STARTER permanently by
                    // activate_subscription, so every customer who had ever
                    // bought one audit saw a green "Current" badge and a green
                    // border on the Starter card forever -- a subscription
                    // claim for a one-off purchase.
                    //
                    // It is also the last instance of exactly what the three
                    // comments above say was removed from this page (no plan
                    // pill, no crown, not "Current Plan"). That pass missed the
                    // badge.
                    //
                    // "Current" now means what it says: the plan this account is
                    // on. Nothing sells a subscription, so that is Free for
                    // everyone, and Starter is marked as a purchase instead.
                    const isCurrentPlan = planLine === 'Free'
                      ? plan.id === 'FREE'
                      : planLine === plan.id;
                    const isInr = gateway === 'razorpay';
                    const basePrice = isInr ? plan.priceInr : plan.priceUsd;
                    const currencySymbol = isInr ? '₹' : '$';
                    const displayPrice = `${currencySymbol}${basePrice}`;
                    const discountedPrice = promoDiscount > 0 && basePrice > 0
                      ? Math.round(basePrice * (1 - promoDiscount / 100))
                      : null;

                    return (
                      <div
                        key={plan.id}
                        className="relative rounded-2xl p-6 transition-all"
                        style={{
                          background: plan.popular ? 'var(--accent-text)' : 'var(--surface-white)',
                          border: isCurrentPlan ? '2px solid var(--success)' : plan.popular ? 'none' : '1px solid var(--border-1)'
                        }}
                        data-testid={`plan-${plan.id}`}
                      >
                        {plan.popular && (
                          <span
                            className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full"
                            style={{ background: 'var(--ink-strong)', color: 'var(--accent-text)' }}
                          >
                            Most Popular
                          </span>
                        )}
                        {isCurrentPlan && (
                          <span
                            className="absolute -top-3 right-4 text-[10px] font-bold px-3 py-1 rounded-full"
                            style={{ background: 'var(--success)', color: 'white' }}
                          >
                            Current
                          </span>
                        )}
                        <h3 className="font-bold text-lg" style={{ color: plan.popular ? 'white' : 'var(--ink-1)' }}>{plan.name}</h3>
                        <div className="mt-2 mb-4">
                          {discountedPrice !== null ? (
                            <>
                              <span className="text-lg line-through" style={{ color: plan.popular ? 'rgba(255,255,255,0.5)' : 'var(--ink-2)' }}>{displayPrice}</span>
                              <span className="text-3xl font-bold ml-2" style={{ color: plan.popular ? 'white' : 'var(--ink-1)' }}>{currencySymbol}{discountedPrice}</span>
                            </>
                          ) : (
                            <span className="text-3xl font-bold" style={{ color: plan.popular ? 'white' : 'var(--ink-1)' }}>{displayPrice}</span>
                          )}
                          {isInr && basePrice > 0 && (
                            <span className="text-xs ml-1" style={{ color: plan.popular ? 'rgba(255,255,255,0.5)' : 'var(--ink-2)' }}>incl. GST</span>
                          )}
                          <span className="text-sm ml-1" style={{ color: plan.popular ? 'rgba(255,255,255,0.5)' : 'var(--ink-2)' }}>{plan.period}</span>
                        </div>
                        <p className="text-sm mb-4" style={{ color: plan.popular ? 'rgba(255,255,255,0.7)' : 'var(--ink-2)' }}>{plan.audits}</p>
                        <ul className="space-y-2 mb-6">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-2 text-sm" style={{ color: plan.popular ? 'rgba(255,255,255,0.8)' : 'var(--ink-2)' }}>
                              <Check className="w-4 h-4" style={{ color: plan.popular ? 'rgba(255,255,255,0.5)' : 'var(--ink-2)' }} />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        {!isCurrentPlan && basePrice > 0 && (
                          <button
                            onClick={() => handleUpgrade(plan.id)}
                            disabled={upgrading === plan.id}
                            className="w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            style={{ 
                              background: plan.popular ? 'white' : 'var(--accent-text)',
                              color: plan.popular ? 'var(--accent-text)' : 'white'
                            }}
                            data-testid={`upgrade-${plan.id}`}
                          >
                            {upgrading === plan.id ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              'Upgrade'
                            )}
                          </button>
                        )}
                        {/* The ENTERPRISE "Contact Sales" branch was removed
                            on 2026-08-31. The plans array above holds FREE and
                            STARTER only, so it could never render — a mailto
                            for a tier with no price, no page and no account. */}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* White-label Tab */}
            {activeTab === 'white-label' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                {!canUseWhiteLabel ? (
                  // Upgrade prompt for non-Pro users
                  <div className="rounded-2xl p-8 text-center" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(168,85,247,0.15)' }}>
                      <Lock className="w-8 h-8" style={{ color: '#A855F7' }} />
                    </div>
                    <h2 className="font-semibold text-xl mb-2" style={{ color: 'var(--ink-1)' }}>White-label Reports</h2>
                    <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--ink-2)' }}>
                      Brand your audit reports with your agency logo and name. Available as a {`$${ADDON_PRICES.white_label.usd}`} add-on on the Starter plan.
                    </p>

                    <div className="rounded-xl p-6 mb-6 max-w-md mx-auto" style={{ background: 'var(--surface-panel)' }}>
                      <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--ink-1)' }}>With White-label you can:</h3>
                      <ul className="space-y-3 text-left">
                        {[
                          'Add your agency logo to PDF reports',
                          'Display your agency name on reports',
                          'Remove UXAuditPro branding',
                          'Share branded reports with clients'
                        ].map((item) => (
                          <li key={item} className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink-2)' }}>
                            <CheckCircle className="w-4 h-4" style={{ color: 'var(--success)' }} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {userPlan === 'FREE' ? (
                      <button
                        onClick={() => setActiveTab('plans')}
                        className="uxa-submit uxa-submit--auto uxa-submit--tertiary"
                        data-testid="upgrade-for-whitelabel"
                      >
                        Upgrade to a paid plan first
                      </button>
                    ) : (
                      <button
                        onClick={handlePurchaseWhiteLabelAddon}
                        disabled={purchasingAddon}
                        className="uxa-submit uxa-submit--auto mx-auto"
                        data-testid="purchase-whitelabel-addon"
                      >
                        {purchasingAddon ? (
                          <><Loader2 className="w-4 h-4 animate-spin" />Processing...</>
                        ) : (
                          `Add White Label — $${ADDON_PRICES.white_label.usd}`
                        )}
                      </button>
                    )}
                  </div>
                ) : (
                  // White-label settings for Pro+ users
                  <div className="rounded-2xl p-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="font-semibold" style={{ color: 'var(--ink-1)' }}>White-label Settings</h2>
                        <p className="text-sm" style={{ color: 'var(--ink-2)' }}>Customize your audit reports with your branding</p>
                        {/* A STATE MESSAGE, NOT A BLOCK, and deliberately not
                            a second gate. Enforcement is already correct and
                            already per-audit: _white_label_paid_for_audit
                            resolves the payment by used_for_audit and
                            branding_applies requires that grant AND this
                            preference. What was missing is that the page never
                            said so, so a customer who bought the add-on once
                            could edit these settings, download a report from a
                            $49-only purchase, and get an unbranded PDF with no
                            explanation. Two of the four add-on holders are in
                            exactly that state today. */}
                        <p className="text-sm mt-2" style={{ color: 'var(--ink-2)' }} data-testid="white-label-scope">
                          {`Branding applies to audits you bought it for. Add it at `
                            + `checkout for $${ADDON_PRICES.white_label.usd} to brand a new one.`}
                        </p>
                      </div>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <span className="text-sm" style={{ color: 'var(--ink-2)' }}>Enable</span>
                        <div 
                          className="w-11 h-6 rounded-full transition-colors relative"
                          style={{ background: whiteLabelSettings.enabled ? 'var(--success)' : '#1A2540' }}
                          onClick={() => setWhiteLabelSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                        >
                          <div 
                            className="absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform"
                            style={{ transform: whiteLabelSettings.enabled ? 'translateX(24px)' : 'translateX(4px)' }}
                          />
                        </div>
                      </label>
                    </div>
                    
                    {/* Agency Name */}
                    <div className="mb-6">
                      <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--ink-2)' }}>Agency Name</label>
                      <input
                        type="text"
                        value={whiteLabelSettings.agency_name}
                        onChange={(e) => setWhiteLabelSettings(prev => ({ ...prev, agency_name: e.target.value }))}
                        placeholder="Your Agency Name"
                        className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent-text)]/30"
                        style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-2)', color: 'var(--ink-1)' }}
                        data-testid="agency-name-input"
                      />
                    </div>
                    
                    {/* Logo Upload */}
                    <div className="mb-6">
                      <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--ink-2)' }}>Agency Logo</label>
                      <div className="rounded-xl p-6 text-center" style={{ border: '2px dashed var(--border-2)' }}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="logo-upload-input"
                        />
                        {logoPreview ? (
                          <div className="flex flex-col items-center gap-3">
                            <div className="relative inline-block">
                              <img src={logoPreview} alt="Logo preview" className="max-h-24 max-w-[200px] object-contain" />
                              <button
                                onClick={handleRemoveLogo}
                                className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
                                style={{ background: 'var(--danger)', color: 'white' }}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                              style={{ background: 'var(--surface-panel)', color: 'var(--ink-2)', border: '1px solid var(--border-2)' }}
                            >
                              <Upload className="w-4 h-4 inline mr-2" />
                              Change Logo
                            </button>
                          </div>
                        ) : (
                          <>
                            <Image className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--ink-2)' }} />
                            <p className="text-sm mb-2" style={{ color: 'var(--ink-2)' }}>Upload your agency logo</p>
                            <p className="text-xs mb-4" style={{ color: '#4A5568' }}>PNG, JPG up to 2MB (recommended: 400x100px)</p>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                              style={{ background: 'var(--surface-panel)', color: 'var(--ink-2)', border: '1px solid var(--border-2)' }}
                            >
                              <Upload className="w-4 h-4 inline mr-2" />
                              Upload Logo
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Preview */}
                    <div className="mb-6 p-4 rounded-xl" style={{ background: 'var(--surface-panel)' }}>
                      <p className="text-xs font-medium mb-3" style={{ color: 'var(--ink-2)' }}>Preview on PDF Report:</p>
                      <div className="rounded-lg p-4" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                        <div className="flex items-center gap-4">
                          {logoPreview ? (
                            <img src={logoPreview} alt="Logo" className="h-8 object-contain" />
                          ) : (
                            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: 'var(--surface-panel)' }}>
                              <Image className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold" style={{ color: 'var(--ink-1)' }}>
                              {whiteLabelSettings.agency_name || 'Your Agency Name'}
                            </p>
                            <p className="text-[10px]" style={{ color: 'var(--ink-2)' }}>UX Audit Report</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Save Button */}
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      {whiteLabelSuccess && (
                        <p className="text-sm flex items-center gap-1" style={{ color: 'var(--success)' }}>
                          <CheckCircle className="w-4 h-4" /> {whiteLabelSuccess}
                        </p>
                      )}
                      <div className="flex items-center gap-3 ml-auto">
                        <button
                          onClick={handlePreviewPDF}
                          disabled={generatingPreview || savingWhiteLabel}
                          className="uxa-submit uxa-submit--auto uxa-submit--secondary"
                          data-testid="preview-pdf-btn"
                        >
                          {generatingPreview ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4" />
                              Preview PDF
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleSaveWhiteLabel}
                          disabled={savingWhiteLabel}
                          className="uxa-submit uxa-submit--auto uxa-submit--tertiary"
                          data-testid="save-whitelabel-btn"
                        >
                          {savingWhiteLabel ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            'Save Settings'
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Disable add-on section */}
                    <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--border-1)' }}>
                      {!disableConfirm ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--ink-1)' }}>Remove White Label Add-on</p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--ink-2)' }}>Disables access immediately. You can re-add it at any time.</p>
                          </div>
                          <button
                            onClick={() => setDisableConfirm(true)}
                            className="px-4 py-2 text-xs font-semibold rounded-lg transition-colors"
                            style={{ border: '1px solid rgba(239,68,68,0.4)', color: 'var(--danger)', background: 'transparent' }}
                            data-testid="disable-whitelabel-btn"
                          >
                            Disable Add-on
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-xl p-4" style={{ background: '#FEF2F2', border: '1px solid rgba(239,68,68,0.2)' }}>
                          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--danger)' }}>Are you sure?</p>
                          <p className="text-xs mb-4" style={{ color: 'var(--ink-2)' }}>
                            Your white label branding will be removed from all future reports immediately.
                          </p>
                          <div className="flex gap-3">
                            <button
                              onClick={handleDisableWhiteLabelAddon}
                              disabled={disablingAddon}
                              className="px-4 py-2 text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2"
                              style={{ background: 'var(--danger)', color: 'white' }}
                              data-testid="confirm-disable-whitelabel"
                            >
                              {disablingAddon ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                              Yes, disable it
                            </button>
                            <button
                              onClick={() => setDisableConfirm(false)}
                              className="px-4 py-2 text-xs font-semibold rounded-lg"
                              style={{ border: '1px solid var(--border-2)', color: 'var(--ink-2)' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Billing Tab */}
            {/* PURCHASES. Was "Billing History", and it was permanently
                empty: the page fetched GET /api/payments/history, that route
                did not exist on any router, allSettled swallowed the 404, and
                every customer -- including ones who had paid -- read
                "No billing history yet / Upgrade to a paid plan to see your
                invoices here".

                Every column here is a field the endpoint sends. Nothing is
                computed from another column, and the amount is NOT formatted
                from the raw `amount`: the two gateways store different units
                (paypal dollars, razorpay paise) and `amount_display` is the
                normalised pair, from backend/delivery.payment_amount. */}
            {activeTab === 'billing' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="rounded-2xl p-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-semibold" style={{ color: 'var(--ink-1)' }}>Purchases</h2>
                  </div>

                  {purchases === null ? (
                    <div className="text-center py-12" data-testid="purchases-unknown">
                      <CreditCard className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--ink-2)' }} />
                      <p className="text-sm mb-2" style={{ color: 'var(--danger)' }}>
                        We could not load your purchases.
                      </p>
                      {/* NOT the empty state. An unreadable history is unknown,
                          not "you never bought anything" -- telling a paying
                          customer the latter is the defect this page came out
                          of. */}
                      <p className="text-xs" style={{ color: 'var(--ink-2)' }}>
                        This is a problem at our end, not a statement about your account.
                      </p>
                    </div>
                  ) : purchases.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--ink-2)' }} />
                      <p className="text-sm mb-4" style={{ color: 'var(--ink-2)' }}>No purchases yet</p>
                      {/* NOT "upgrade to a paid plan". There is no paid plan to
                          upgrade to; there is a $49 audit to buy. */}
                      <p className="text-xs mb-6" style={{ color: 'var(--ink-2)' }}>
                        Your free monthly audit does not appear here. Audits you buy will.
                      </p>
                      <button onClick={() => setActiveTab('plans')} className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
                        View audits
                        <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-1)' }}>
                            <th className="text-left py-3 text-xs font-semibold uppercase" style={{ color: 'var(--ink-2)' }}>Date</th>
                            <th className="text-left py-3 text-xs font-semibold uppercase" style={{ color: 'var(--ink-2)' }}>Amount</th>
                            <th className="text-left py-3 text-xs font-semibold uppercase" style={{ color: 'var(--ink-2)' }}>Paid with</th>
                            <th className="text-left py-3 text-xs font-semibold uppercase" style={{ color: 'var(--ink-2)' }}>Status</th>
                            <th className="text-left py-3 text-xs font-semibold uppercase" style={{ color: 'var(--ink-2)' }}>Audit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchases.map((purchase, i) => {
                            const amount = purchase.amount || {};
                            const isPaid = purchase.status === 'completed';
                            const key = purchase.payment_id || purchase.order_id || `${purchase.created_at}-${i}`;
                            return (
                            <tr key={key} style={{ borderBottom: '1px solid var(--border-1)' }} data-testid={`purchase-${i}`}>
                              <td className="py-4 text-sm" style={{ color: 'var(--ink-2)' }}>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                                  {purchase.created_at ? new Date(purchase.created_at).toLocaleDateString() : '\u2014'}
                                </div>
                              </td>
                              <td className="py-4 text-sm font-semibold" style={{ color: 'var(--ink-1)' }}>
                                {amount.major === null || amount.major === undefined
                                  ? '\u2014'
                                  : `${amount.currency} ${Number(amount.major).toFixed(2)}`}
                                {purchase.is_promo && (
                                  <span className="text-xs ml-1" style={{ color: 'var(--ink-2)' }}>(promo)</span>
                                )}
                                {purchase.discount_percent > 0 && (
                                  <span className="text-xs ml-1" style={{ color: 'var(--success)' }}>(-{purchase.discount_percent}%)</span>
                                )}
                              </td>
                              {/* The PROVIDER, not a hardcoded "PayPal Txn ID"
                                  column. The provider is which collection the
                                  row came from; the page cannot infer it. */}
                              <td className="py-4 text-sm capitalize" style={{ color: 'var(--ink-2)' }}>
                                {purchase.is_promo ? 'promo code' : (purchase.provider || '\u2014')}
                              </td>
                              <td className="py-4">
                                <span
                                  className="px-2 py-1 text-[10px] font-semibold rounded-full capitalize"
                                  style={{
                                    background: isPaid ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                                    color: isPaid ? 'var(--success)' : 'var(--severity-major-text)'
                                  }}
                                >
                                  {isPaid ? 'paid' : (purchase.status || 'pending')}
                                </span>
                              </td>
                              <td className="py-4 text-sm">
                                {purchase.audit?.report_url ? (
                                  <Link
                                    to={purchase.audit.report_url}
                                    className="font-medium hover:underline"
                                    style={{ color: 'var(--accent-text)' }}
                                    data-testid={`purchase-report-${i}`}
                                  >
                                    {purchase.audit.app_name || 'View report'}
                                  </Link>
                                ) : purchase.audit ? (
                                  /* An audit exists but has no report to open.
                                     Linking a FAILED audit sends the customer
                                     to an error page from their own receipt. */
                                  <span style={{ color: 'var(--ink-2)' }}>
                                    {purchase.audit.app_name || 'Audit'} ({String(purchase.audit.status || '').toLowerCase()})
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--ink-2)' }}>not used yet</span>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'help' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="rounded-2xl p-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
                  <h2 className="font-semibold mb-6" style={{ color: 'var(--ink-1)' }}>Help & Support</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {helpTopics.map((topic) => (
                      <a
                        key={topic.title}
                        href={topic.link}
                        className="flex items-start gap-4 p-4 rounded-xl transition-all duration-200 group"
                        /* --surface-panel on a white card is 1.08:1. The border is the
                           second carrier, exactly as the spec gives cards one. Added
                           2026-08-19 when the generalised guard found it. */
                        style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-2)' }}
                      >
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
                        >
                          <topic.icon className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-sm mb-1 flex items-center gap-1" style={{ color: 'var(--ink-1)' }}>
                            {topic.title}
                            <ArrowUpRight className="w-3.5 h-3.5 transition-colors" style={{ color: 'var(--ink-2)' }} />
                          </h3>
                          <p className="text-xs" style={{ color: 'var(--ink-2)' }}>{topic.desc}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>

                <div 
                  className="rounded-2xl p-6 text-center"
                  style={{ background: 'linear-gradient(135deg, var(--surface-panel) 0%, #FFFFFF 100%)', border: '1px solid var(--border-2)' }}
                >
                  <Mail className="w-10 h-10 mx-auto mb-4" style={{ color: 'var(--ink-2)' }} />
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--ink-1)' }}>Need more help?</h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--ink-2)' }}>Our support team is here to help you succeed</p>
                  <a href="mailto:support@uxauditpro.com" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
                    Contact Support
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </a>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
